import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, Star, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { classNames } from '@/lib/format';
import { useI18n } from '@/providers/I18nProvider';
import { useAuth } from '@/providers/AuthProvider';
import { isAdmin } from '@/lib/permissions';
import { useFavorites } from '@/hooks/useFavorites';
import { MAIN_MENU, ADMIN_MENU, isGroup, flatMenu, type MenuItem, type MenuGroup } from './menuConfig';

const COLLAPSE_KEY = 'kispro-sidebar-collapsed';
const GROUP_OPEN_KEY = 'kispro-sidebar-groups-open';

interface Props {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }: Props) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { favorites, toggle, isFavorite } = useFavorites();
  const location = useLocation();
  const showAdmin = isAdmin(user);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(GROUP_OPEN_KEY) || '{}'); } catch { return {}; }
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(GROUP_OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const favItems = useMemo(() => {
    const all = flatMenu();
    return favorites.map((k) => all.find((x) => x.key === k)).filter(Boolean) as MenuItem[];
  }, [favorites]);

  // Mobile-Schließen bei Routenwechsel
  useEffect(() => { onCloseMobile(); }, [location.pathname, onCloseMobile]);

  return (
    <aside className={classNames('sidebar', collapsed && 'sidebar--collapsed', mobileOpen && 'sidebar--mobile-open')}>
      <div className="sidebar__header">
        <img
          className="sidebar__logo-full"
          src={`${import.meta.env.VITE_DOWNLOADS_BASE_URL}/assets/logo/KISpro_Logo_v1_RGB_darkmode.svg`}
          alt="KISpro"
        />
        <img
          className="sidebar__logo-mark"
          src={`${import.meta.env.VITE_DOWNLOADS_BASE_URL}/assets/logo/KISpro_Signet_RGB.svg`}
          alt="KISpro"
        />
      </div>

      <nav className="sidebar__nav">
        {favItems.length > 0 ? (
          <div className="sidebar__section">
            <div className="sidebar__section-title">{t('nav.favorites')}</div>
            {favItems.map((it) => (
              <Item key={`fav-${it.key}`} item={it} favorite onToggleFav={() => toggle(it.key)} />
            ))}
          </div>
        ) : null}

        <div className="sidebar__section">
          {MAIN_MENU.map((entry) => {
            if (isGroup(entry)) {
              return (
                <Group
                  key={entry.key}
                  group={entry}
                  open={openGroups[entry.key] ?? false}
                  onToggleOpen={() => toggleGroup(entry.key)}
                  isFav={isFavorite}
                  onToggleFav={toggle}
                />
              );
            }
            return (
              <Item
                key={entry.key}
                item={entry}
                favorite={isFavorite(entry.key)}
                onToggleFav={() => toggle(entry.key)}
              />
            );
          })}
        </div>

        {showAdmin ? (
          <div className="sidebar__section">
            <div className="sidebar__section-title">{t('nav.admin')}</div>
            {ADMIN_MENU.map((it) => (
              <Item key={it.key} item={it} favorite={isFavorite(it.key)} onToggleFav={() => toggle(it.key)} />
            ))}
          </div>
        ) : null}
      </nav>

      <div className="sidebar__footer">
        <button className="sidebar__collapse-btn" onClick={onToggleCollapse} aria-label={t('nav.collapse')}>
          {collapsed ? <PanelLeftOpen size={16} aria-hidden /> : <PanelLeftClose size={16} aria-hidden />}
          {!collapsed && <span>{t('nav.collapse')}</span>}
        </button>
      </div>
    </aside>
  );
}

function Item({ item, favorite, onToggleFav }: {
  item: MenuItem;
  favorite?: boolean;
  onToggleFav?: () => void;
}) {
  const { t } = useI18n();
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) => classNames('sidebar__item', isActive && 'sidebar__item--active')}
    >
      <span className="sidebar__item-icon">{item.icon}</span>
      <span className="sidebar__item-label">{t(item.label)}</span>
      {onToggleFav ? (
        <button
          className={classNames('sidebar__star', favorite && 'sidebar__star--active')}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(); }}
          aria-label={favorite ? t('nav.unfavorite') : t('nav.favorite')}
          type="button"
        >
          <Star size={14} fill={favorite ? 'currentColor' : 'none'} aria-hidden />
        </button>
      ) : null}
    </NavLink>
  );
}

function Group({
  group, open, onToggleOpen, isFav, onToggleFav,
}: {
  group: MenuGroup;
  open: boolean;
  onToggleOpen: () => void;
  isFav: (k: string) => boolean;
  onToggleFav: (k: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div>
      <button
        type="button"
        className={classNames('sidebar__item', open && 'sidebar__item--open')}
        onClick={onToggleOpen}
      >
        <span className="sidebar__item-icon">{group.icon}</span>
        <span className="sidebar__item-label">{t(group.label)}</span>
        <ChevronRight className="sidebar__item-arrow" size={14} aria-hidden />
      </button>
      {open ? (
        <div className="sidebar__sub">
          {group.items.map((it) => (
            <Item key={it.key} item={it} favorite={isFav(it.key)} onToggleFav={() => onToggleFav(it.key)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { COLLAPSE_KEY };
